import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import {
  Form,
  InputField,
  InputNumberField,
  DatePickerField,
  SwitchField,
  CurrencyInputField,
} from "@mimber/forms"
import {
  useCreateEventTicketTier,
  useUpdateEventTicketTier,
} from "@mimber/client/mimber-organization"
import {
  EventTicketTierFormSchema,
  type EventTicketTierFormData,
} from "./EventTicketTierForm.schema"
import type { EventTicketTierFormProps } from "./EventTicketTierForm.types"

export const EventTicketTierForm: React.FunctionComponent<
  EventTicketTierFormProps
> = ({ eventUuid, tier, onSuccess, onCancel }) => {
  const isEdit = !!tier

  const form = useForm<EventTicketTierFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(EventTicketTierFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      name: tier?.name ?? "",
      description: tier?.description ?? "",
      price: tier?.price ?? 500,
      capacity: tier?.capacity ?? undefined,
      sortOrder: tier?.sortOrder ?? 0,
      salesStartAt: tier?.salesStartAt ?? undefined,
      salesEndAt: tier?.salesEndAt ?? undefined,
      isActive: tier?.isActive ?? true,
    },
  })

  const createMutation = useCreateEventTicketTier(eventUuid, {
    onSuccess: () => {
      message.success("Ticket tier created")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to create ticket tier: ${error.message}`)
    },
  })

  const updateMutation = useUpdateEventTicketTier(eventUuid, {
    onSuccess: () => {
      message.success("Ticket tier updated")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to update ticket tier: ${error.message}`)
    },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  const onSubmit = (data: EventTicketTierFormData) => {
    if (isEdit && tier) {
      updateMutation.mutate({ tierUuid: tier.uuid, data })
    } else {
      createMutation.mutate(data)
    }
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Tier Name"
        placeholder="e.g., General Admission, VIP"
        required
      />

      <InputField
        name="description"
        label="Description"
        placeholder="Optional description for this tier"
      />

      <CurrencyInputField
        name="price"
        label="Price"
        placeholder="0.00"
        required
        minCents={50}
      />

      <InputNumberField
        name="capacity"
        label="Capacity"
        placeholder="Leave empty for unlimited"
        min={1}
      />

      <InputNumberField name="sortOrder" label="Sort Order" min={0} />

      <DatePickerField name="salesStartAt" label="Sales Start" showTime />

      <DatePickerField name="salesEndAt" label="Sales End" showTime />

      <SwitchField name="isActive" label="Active" />

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" htmlType="submit" loading={isPending}>
          {isEdit ? "Save Changes" : "Create Tier"}
        </Button>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
      </Space>
    </Form>
  )
}
